import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePageMetadata } from "../context/PageMetadataContext";
import { useAuth } from "../context/AuthContext";
import { useAnalytics } from "../context/AnalyticsContext";
import { AppButton } from "../components/ui";
import "./Viewer.scss";

const VIEWER_BASE_URL = process.env.REACT_APP_VIEWER_BASE_URL || "https://viewer.10k.science";

const Viewer = () => {
  const navigate = useNavigate();
  const { experienceId: experienceIdFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const { setTitle, setShowTitleInHeader } = usePageMetadata();
  const { userName, userId, organizationId, subscriptionStatus } = useAuth();
  const { markAnalyticsDirty, scheduleAnalyticsRefresh } = useAnalytics();

  useEffect(() => {
    window.scrollTo(0, 0);
    setShowTitleInHeader(false);
    setTitle("Viewer");
  }, [setShowTitleInHeader, setTitle]);

  useEffect(() => {
    markAnalyticsDirty();

    return () => {
      markAnalyticsDirty();
      scheduleAnalyticsRefresh({ delayMs: 4000 });
    };
  }, [markAnalyticsDirty, scheduleAnalyticsRefresh]);

  const experienceIdFromQuery = searchParams.get("experienceId");

  const experienceId = useMemo(
    () => experienceIdFromPath || experienceIdFromQuery || "",
    [experienceIdFromPath, experienceIdFromQuery]
  );

  const embedUrl = useMemo(() => {
    if (!experienceId) {
      return "";
    }

    const viewerParams = new URLSearchParams({
      userName: userName || "",
      userId: userId || "",
      organizationId: organizationId || "",
      subscriptionStatus: subscriptionStatus || "",
    });

    console.log(`${VIEWER_BASE_URL}/experiences/embed/${encodeURIComponent(experienceId)}?${viewerParams.toString()}`);
    return `${VIEWER_BASE_URL}/experiences/embed/${encodeURIComponent(experienceId)}?${viewerParams.toString()}`;
  }, [experienceId, userName, userId, organizationId, subscriptionStatus]);

  const openExitDialog = useCallback(() => {
    setIsExitDialogOpen(true);
  }, []);

  const closeExitDialog = useCallback(() => {
    setIsExitDialogOpen(false);
  }, []);

  const handleConfirmBackToHome = useCallback(() => {
    setIsExitDialogOpen(false);
    navigate("/");
  }, [navigate]);

  return (
    <main className="viewerPage">
      <section className="viewerPage__content">
        <div className="viewerPage__overlayControls">
          <AppButton
            className="viewerPage__backButton"
            kind="secondary"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={openExitDialog}
          >
            Back to Home
          </AppButton>
        </div>

        {!embedUrl && (
          <div className="viewerPage__status">
            Select an experience from Home to launch the embedded viewer.
          </div>
        )}

        {embedUrl && (
          <div className="viewerPage__embed">
            <iframe
              src={embedUrl}
              title="10k Web Viewer"
              loading="lazy"
              allow="camera; microphone; fullscreen"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}

        <Dialog
          open={isExitDialogOpen}
          onClose={closeExitDialog}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            className: "viewerExitDialog__paper",
          }}
        >
          <DialogTitle className="viewerExitDialog__title">Quit preview?</DialogTitle>
          <DialogContent className="viewerExitDialog__content">
            Quit previewing the experience and go back home?
          </DialogContent>
          <DialogActions className="viewerExitDialog__actions">
            <AppButton kind="ghost" onClick={closeExitDialog}>
              Keep Previewing
            </AppButton>
            <AppButton kind="primary" onClick={handleConfirmBackToHome}>
              Back to Home
            </AppButton>
          </DialogActions>
        </Dialog>
      </section>
    </main>
  );
};

export default Viewer;
